<script setup lang="ts">
import { getApostatudoApiUrl } from '~/utils/apostatudo/get-apostatudo-api-url';
import { getApostatudoAuthHeaders } from '~/utils/apostatudo/get-apostatudo-auth-headers';

// Seção "Estatísticas" do dashboard Perfil — redesenhada pro Figma node
// 23508:113345 ("bonus-cards"): 3 cards de bônus recebido (giros, cashback,
// saldo), cada um com gradiente/cor/ícone próprios. Substitui o grid antigo
// de 6 tiles genéricos (Figma 19771:42296/19771:52185) — os outros 3 stats
// (Ganhos totais, Apostas totais, Vitórias) saíram, não têm mais lugar
// nessa tela. Extraída do ScreenProfile.vue pra não inflar mais aquele
// arquivo; autocontida, mesmo padrão do UserV2BalancesBar.

// Ícones baixados do Figma (não existiam no projeto, ver public/images/
// user-v2/stat-{spins,cashback,bonus}.png) e redimensionados pra 120x120,
// mesmo padrão do stat-trophy.png que eles substituem.
//
// Mobile (node 23508:113391) tem layout diferente do desktop (vertical,
// gradiente radial, ícone grande embaixo) e o Figma usa cor levemente
// diferente do desktop pro rótulo de cashback (#4ade80, não #86efac).
// Mantive as duas versões fiéis ao que está desenhado em cada breakpoint,
// mesmo com a pequena inconsistência entre elas (parece só deriva do
// próprio Figma).

// GET /players/bonus?user_id=... — lifetime + janela de 7 dias + janela de
// 12 meses do jogador. Chamado toda vez que o modal abre: a screen desmonta/
// remonta a cada open/close (ver Modal.vue), então o onMounted abaixo já
// cobre isso sozinho. Mesma URL/auth de /players/me (Apostatudo API, não o
// BFF genérico) — getApostatudoApiUrl()/getApostatudoAuthHeaders(), ver
// adapters/api-player-profile/apostatudo-api-fetch-player-profile.adapter.ts.
interface PlayerBonusEntry {
  amount: string;
}

interface PlayerBonus {
  user_id: number;
  lifetime: {
    cashback: string;
    bonus_balance: string;
    free_spins: string;
  };
  days: Array<PlayerBonusEntry & { date: string }>;
  months: Array<PlayerBonusEntry & { month: string }>;
}

const { userId, fetchUserProfile } = useUserData();

const playerBonus = ref<PlayerBonus | null>(null);
const isLoadingBonus = ref(true);

async function getPlayerBonus() {
  isLoadingBonus.value = true;

  await fetch(
    `${getApostatudoApiUrl()}/players/bonus?user_id=${userId.value}`,
    {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        ...getApostatudoAuthHeaders(),
      },
    },
  )
    .then((response) => {
      if (!response.ok)
        throw new Error(`PlayerBonus failed: ${response.status}`);

      return response.json();
    })
    .then((data) => {
      playerBonus.value = data;
    })
    .catch(() => {
      playerBonus.value = null;
    })
    .finally(() => {
      isLoadingBonus.value = false;
    });
}

onMounted(async () => {
  await fetchUserProfile();
  getPlayerBonus();
});

// nunca 404 — quem nunca teve bônus vem com days/months vazios (sempre
// juntos). Não é erro nem empty state: a seção renderiza ZERADA (cards em
// R$ 0,00 e gráfico reto no zero), com uma série sintética de 7 dias / 12
// meses terminando hoje pra o gráfico ter eixo X e pontos como sempre.
// Também cobre falha na chamada (playerBonus null).
const PLACEHOLDER_DAYS = 7;
const PLACEHOLDER_MONTHS = 12;

// mesma ordem da API (mais recente primeiro) — buildGrowth inverte.
function placeholderDays() {
  const today = new Date();

  return Array.from({ length: PLACEHOLDER_DAYS }, (_, index) => {
    const date = new Date(today);

    date.setUTCDate(today.getUTCDate() - index);

    return { date: date.toISOString().slice(0, 10), amount: '0.00' };
  });
}

function placeholderMonths() {
  const today = new Date();

  return Array.from({ length: PLACEHOLDER_MONTHS }, (_, index) => {
    const month = new Date(
      Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - index, 1),
    );

    return { month: month.toISOString().slice(0, 10), amount: '0.00' };
  });
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

// dinheiro chega como string ("1.50"), nunca number — Number() só serve pra
// EXIBIR um valor por vez (o que o Intl.NumberFormat faz aqui). Nunca somar
// vários valores em Number, perde centavo.
function formatCurrency(amount: string) {
  return currencyFormatter.format(Number(amount));
}

// Folga extra (px) entre o valor "R$ ..." e a arte nos cards mobile, somada
// ao top do Figma de cada card. O bloco de texto termina em y=35 (6 + 14 + 1
// + 14) e os PNGs têm 1–3px de transparência no topo, então o que o olho vê
// de espaço é ~ (top + transparência − 33). 4px dá uma folga clara.
const MOBILE_ICON_GAP = 4;

const bonusCards = computed(() => {
  // lifetime some enquanto carrega/sem sessão — zera em vez de quebrar o
  // formatCurrency.
  const lifetime = playerBonus.value?.lifetime ?? {
    cashback: '0.00',
    bonus_balance: '0.00',
    free_spins: '0.00',
  };

  return [
    {
      label: 'Freespins recebidos',
      // free_spins é o valor DISPONIBILIZADO em giros grátis, não o que o
      // jogador ganhou jogando eles — "Giros grátis" em vez de "Ganho com
      // giros" pra não afirmar o contrário do que o dado significa.
      labelMobile: 'Giros grátis',
      value: formatCurrency(lifetime.free_spins),
      labelColor: '#fde047',
      glow: '#F5C451',
      gradient:
        'linear-gradient(90deg, rgba(245,196,81,0) 0%, rgba(245,196,81,0.05) 55%, rgba(245,196,81,0.22) 100%), linear-gradient(90deg, #713F12 0%, #713F12 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(245,196,81,0.22) 0%, rgba(245,196,81,0.05) 45%, rgba(245,196,81,0) 100%), linear-gradient(90deg, #713F12 0%, #713F12 100%)',
      icon: '/images/user-v2/stat-spins.png',
      // mobile (Figma 23508:113392-4): cada card tem a arte num tamanho e
      // altura próprios — a do bônus é a maior e todas vazam pelo rodapé.
      // top é o do Figma; a folga extra entre o "R$" e a arte é
      // MOBILE_ICON_GAP, somada no template.
      iconSizeMobile: 69,
      iconTopMobile: 36,
    },
    {
      label: 'Cashback Total',
      labelMobile: 'Cashback total',
      value: formatCurrency(lifetime.cashback),
      labelColor: '#86efac',
      labelColorMobile: '#4ade80',
      glow: '#17C964',
      gradient:
        'linear-gradient(90deg, rgba(23,201,100,0) 0%, rgba(23,201,100,0.05) 55%, rgba(23,201,100,0.22) 100%), linear-gradient(90deg, #14532D 0%, #14532D 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(23,201,100,0.22) 0%, rgba(23,201,100,0.05) 45%, rgba(23,201,100,0) 100%), linear-gradient(90deg, #14532D 0%, #14532D 100%)',
      icon: '/images/user-v2/stat-cashback.png',
      iconSizeMobile: 75,
      iconTopMobile: 32,
    },
    {
      label: 'Saldo bônus',
      labelMobile: 'Saldo bônus',
      value: formatCurrency(lifetime.bonus_balance),
      labelColor: '#67e8f9',
      glow: '#42ACFF',
      gradient:
        'linear-gradient(90deg, rgba(66,172,255,0) 0%, rgba(66,172,255,0.05) 55%, rgba(66,172,255,0.22) 100%), linear-gradient(90deg, #0D0D90 0%, #0D0D90 100%)',
      gradientMobile:
        'radial-gradient(120% 70% at 50% 100%, rgba(66,172,255,0.22) 0%, rgba(66,172,255,0.05) 45%, rgba(66,172,255,0) 100%), linear-gradient(90deg, #0D0D90 0%, #0D0D90 100%)',
      icon: '/images/user-v2/stat-bonus.png',
      iconSizeMobile: 83,
      iconTopMobile: 32,
    },
  ];
});

const MONTH_LABELS = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

// `?? ''`: com noUncheckedIndexedAccess o índice devolve `string | undefined`
// e buildGrowth espera `string`. Só cai no '' se a data vier inválida (NaN).
function monthLabel(month: string) {
  return MONTH_LABELS[new Date(month).getUTCMonth()] ?? '';
}

const WEEKDAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function weekdayLabel(date: string) {
  return WEEKDAY_LABELS[new Date(date).getUTCDay()] ?? '';
}

// entries vem do mais recente pro mais antigo — inverte pra desenhar da
// esquerda (mais antigo) pra direita (mais recente), como o gráfico espera.
function buildGrowth(
  entries: { amount: string }[],
  toLabel: (entry: any) => string,
) {
  const chronological = [...entries].reverse();

  // gráfico mostra o valor de CADA período isolado, não acumulado — só o
  // "Bônus do período" (delta) abaixo é a soma da janela inteira.
  const points = chronological.map((entry) => ({
    day: toLabel(entry),
    value: Math.max(0, Number(entry.amount)),
  }));

  const total = points.reduce((sum, point) => sum + point.value, 0);

  return {
    delta: `${total >= 0 ? '+' : '-'}${currencyFormatter.format(Math.abs(total))}`,
    points,
  };
}

const monthlyGrowth = computed(() => {
  const months = playerBonus.value?.months ?? [];

  return buildGrowth(months.length ? months : placeholderMonths(), (entry) =>
    monthLabel(entry.month),
  );
});

const weeklyGrowth = computed(() => {
  const days = playerBonus.value?.days ?? [];

  return buildGrowth(days.length ? days : placeholderDays(), (entry) =>
    weekdayLabel(entry.date),
  );
});

// dropdown "Semanal/Mensal" do header do gráfico — padrão mensal (obs do
// ENG-362), troca pra semanal (days) e volta.
const GROWTH_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'weekly', label: 'Semanal' },
] as const;

const growthPeriod = ref<'monthly' | 'weekly'>('monthly');

const growthPeriodLabel = computed(
  () =>
    GROWTH_PERIOD_OPTIONS.find((option) => option.value === growthPeriod.value)
      ?.label,
);

const periodBonus = computed(() =>
  growthPeriod.value === 'monthly' ? monthlyGrowth.value : weeklyGrowth.value,
);

// ----- Gráfico "Bônus do período" (SVG inline, sem lib) -----
const CHART_W = 303;
const CHART_H = 64;
const CHART_PAD_X = 7;
const CHART_PAD_Y = 8;

// eixo Y escalado pelo maior valor real (antes assumia range fixo 0-100)
const chartMaxValue = computed(() =>
  Math.max(...periodBonus.value.points.map((point) => point.value), 1),
);

const chartPoints = computed(() =>
  periodBonus.value.points.map((point, index) => ({
    day: point.day,
    value: point.value,
    x:
      CHART_PAD_X +
      (index * (CHART_W - CHART_PAD_X * 2)) /
        (periodBonus.value.points.length - 1),
    y:
      CHART_H -
      CHART_PAD_Y -
      (point.value / chartMaxValue.value) * (CHART_H - CHART_PAD_Y * 2),
  })),
);

const chartLine = computed(() =>
  chartPoints.value.map((p) => `${p.x},${p.y}`).join(' '),
);

const chartArea = computed(
  () =>
    `${CHART_PAD_X},${CHART_H} ${chartLine.value} ${CHART_W - CHART_PAD_X},${CHART_H}`,
);

// balão explicando o delta — hover no desktop, toggle de clique no mobile
// (não tem :hover de verdade em touch, então o clique cobre os dois).
const showDeltaHint = ref(false);

const deltaHintText = computed(() =>
  growthPeriod.value === 'monthly'
    ? 'Soma do bônus ganho nos últimos 12 meses.'
    : 'Soma do bônus ganho nos últimos 7 dias.',
);

// tooltip com o valor do ponto, ao passar o mouse
const hoveredPointIndex = ref<number | null>(null);

const hoveredPoint = computed(() =>
  hoveredPointIndex.value !== null
    ? chartPoints.value[hoveredPointIndex.value]
    : null,
);
</script>

<template>
  <section class="flex w-full flex-col gap-[10px]">
    <div
      class="flex h-[32px] w-full items-center rounded-[12px] bg-v2-default-300 pl-[12px]"
    >
      <!-- mesmo texto e mesma tipografia do cabeçalho "Atalhos" (ScreenProfile)
           nos dois breakpoints: 14px no mobile, 12px no desktop -->
      <p
        class="text-[14px] font-medium leading-[16px] text-white md:text-[12px]"
      >
        Estatísticas e bônus recebidos
      </p>
    </div>

    <div
      class="border-solid flex w-full flex-col gap-[10px] rounded-[16px] border border-v2-default-300 p-[16px]"
    >
      <!-- loading -->
      <template v-if="isLoadingBonus">
        <div class="flex w-full gap-[10px]">
          <div
            v-for="n in 3"
            :key="n"
            class="h-[92px] flex-1 animate-pulse rounded-[12px] bg-v2-default/40 lg:h-[64px]"
          />
        </div>
        <div
          class="h-[120px] w-full animate-pulse rounded-[12px] bg-v2-default/40"
        />
      </template>

      <!-- sem bônus (days/months vazios) ou falha: renderiza zerado, ver
           placeholderDays/placeholderMonths no script -->
      <template v-else>
        <!-- mobile (node 23508:113391): vertical, gradiente radial, ícone
             grande embaixo — layout bem diferente do desktop pra caber a
             mesma info num card mais estreito -->
        <div class="flex w-full gap-[10px] lg:hidden">
          <div
            v-for="card in bonusCards"
            :key="card.label"
            class="relative h-[92px] min-w-0 flex-1 overflow-hidden rounded-[12px]"
            :style="{ backgroundImage: card.gradientMobile }"
          >
            <div
              class="absolute inset-x-[8px] top-[6px] z-10 flex flex-col items-center gap-[1px]"
            >
              <p
                class="h-[14px] text-center text-[10px] font-bold leading-[14px]"
                :style="{ color: card.labelColorMobile || card.labelColor }"
              >
                {{ card.labelMobile }}
              </p>
              <p
                class="whitespace-nowrap text-[11px] font-extrabold leading-[14px] text-white"
              >
                {{ card.value }}
              </p>
            </div>

            <!-- glow 77px centrado em y=70 (metade pra fora do card) + a arte
                 com o tamanho/posição de cada card no Figma, cortada pelo
                 overflow-hidden — mesmos PNGs do desktop, só a geometria muda -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute left-1/2 top-[70px] size-[77px] -translate-x-1/2 rounded-full opacity-45 blur-[14px]"
              :style="{ backgroundColor: card.glow }"
            />
            <img
              alt=""
              class="pointer-events-none absolute left-1/2 -translate-x-1/2 object-contain"
              :src="getAssetsUrlForCDN(card.icon)"
              :style="{
                width: `${card.iconSizeMobile}px`,
                height: `${card.iconSizeMobile}px`,
                top: `${card.iconTopMobile + MOBILE_ICON_GAP}px`,
              }"
            />
          </div>
        </div>

        <!-- desktop (node 23508:113345): horizontal, gradiente linear, ícone
           pequeno no canto -->
        <div class="hidden w-full gap-[10px] lg:flex">
          <div
            v-for="card in bonusCards"
            :key="card.label"
            class="relative flex min-w-0 flex-1 items-center gap-[8px] overflow-hidden rounded-[12px] p-[10px]"
            :style="{ backgroundImage: card.gradient }"
          >
            <div class="flex min-w-0 flex-col gap-[2px]">
              <p
                class="truncate text-[12px] font-bold leading-none"
                :style="{ color: card.labelColor }"
              >
                {{ card.label }}
              </p>
              <p
                class="truncate text-[14px] font-extrabold leading-none text-white"
              >
                {{ card.value }}
              </p>
            </div>

            <!-- glow: mancha desfocada atrás do ícone — CSS em vez de baixar
               o SVG do Figma, é só um círculo com blur, mais barato -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute -right-[10px] top-[8px] size-[54px] rounded-full opacity-45 blur-xl"
              :style="{ backgroundColor: card.glow }"
            />
            <img
              alt=""
              class="pointer-events-none absolute -right-[6px] top-0 size-[54px] object-contain"
              :src="getAssetsUrlForCDN(card.icon)"
            />
          </div>
        </div>

        <!-- Bônus do período -->
        <div
          class="flex w-full flex-col items-center gap-[6px] rounded-[12px] bg-v2-default-300 px-[6px] pb-[12px] pt-[6px]"
        >
          <div class="flex w-full items-center justify-between pl-[6px]">
            <div
              class="flex items-center gap-[6px] text-[12px] font-bold leading-[16px]"
            >
              <p class="text-white">Bônus do período</p>
              <p class="text-v2-success">
                {{ periodBonus.delta }}
              </p>
              <span
                class="relative inline-flex size-[14px] shrink-0 items-center justify-center"
                @click="showDeltaHint = !showDeltaHint"
                @mouseenter="showDeltaHint = true"
                @mouseleave="showDeltaHint = false"
              >
                <svg
                  class="size-[12px] text-white/40"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  viewBox="0 0 16 16"
                >
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                  />
                  <line
                    x1="8"
                    x2="8"
                    y1="7"
                    y2="11.5"
                  />
                  <line
                    x1="8"
                    x2="8"
                    y1="4.5"
                    y2="4.5"
                  />
                </svg>

                <div
                  v-if="showDeltaHint"
                  class="absolute right-0 top-full z-10 mt-[6px] w-[180px] max-w-[70vw] rounded-[8px] bg-v2-default-500 p-[8px] text-[11px] font-normal leading-[15px] text-white lg:right-auto lg:left-0 lg:max-w-none"
                >
                  {{ deltaHintText }}
                </div>
              </span>
            </div>
            <!-- dropdown custom (Headless UI, já usado em base/form/Select.vue) —
               select nativo não deixa estilizar borda/raio da lista aberta -->
            <HeadlessListbox v-model="growthPeriod">
              <div class="relative">
                <HeadlessListboxButton
                  class="flex cursor-pointer items-center gap-[5px] rounded-[9px] px-[10px] py-[6px] text-[12px] font-semibold leading-[16px] text-v2-cyan-600 outline-none"
                >
                  {{ growthPeriodLabel }}
                  <svg
                    class="size-[10px]"
                    fill="none"
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 10 10"
                  >
                    <path d="m2 3.5 3 3 3-3" />
                  </svg>
                </HeadlessListboxButton>

                <HeadlessListboxOptions
                  class="border-solid absolute right-0 z-10 mt-[6px] flex w-[110px] flex-col gap-[4px] overflow-hidden rounded-[12px] border-[0.5px] border-v2-default-500 bg-v2-default-300 p-[4px] outline-none"
                >
                  <HeadlessListboxOption
                    v-for="option in GROWTH_PERIOD_OPTIONS"
                    :key="option.value"
                    v-slot="{ active, selected }"
                    as="template"
                    :value="option.value"
                  >
                    <li
                      class="cursor-pointer rounded-[8px] px-[10px] py-[8px] text-[12px] font-semibold"
                      :class="{
                        'bg-v2-primary text-white': selected,
                        'bg-white/10 text-white': active && !selected,
                        'text-v2-foreground': !active && !selected,
                      }"
                    >
                      {{ option.label }}
                    </li>
                  </HeadlessListboxOption>
                </HeadlessListboxOptions>
              </div>
            </HeadlessListbox>
          </div>

          <div class="w-full px-[6px]">
            <div
              class="relative w-full"
              :style="{ height: `${CHART_H}px` }"
            >
              <svg
                class="absolute inset-0 size-full"
                preserveAspectRatio="none"
                :viewBox="`0 0 ${CHART_W} ${CHART_H}`"
              >
                <polygon
                  class="fill-v2-success/15"
                  :points="chartArea"
                />
                <polyline
                  class="stroke-v2-success"
                  fill="none"
                  :points="chartLine"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  vector-effect="non-scaling-stroke"
                />
              </svg>
              <span
                v-for="(point, index) in chartPoints"
                :key="point.day"
                class="absolute size-[8px] -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-v2-success"
                :style="{
                  left: `${(point.x / CHART_W) * 100}%`,
                  top: `${point.y}px`,
                }"
                @mouseenter="hoveredPointIndex = index"
                @mouseleave="hoveredPointIndex = null"
              />
              <div
                v-if="hoveredPoint"
                class="pointer-events-none absolute -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-[6px] bg-v2-default-500 px-[6px] py-[2px] text-[10px] font-semibold text-white"
                :style="{
                  left: `${(hoveredPoint.x / CHART_W) * 100}%`,
                  top: `${hoveredPoint.y - 8}px`,
                }"
              >
                {{ currencyFormatter.format(hoveredPoint.value) }}
              </div>
            </div>
            <div
              class="mt-[4px] flex w-full items-center justify-between text-center text-[10px] font-semibold leading-[16px] text-[rgba(212,212,216,0.4)]"
            >
              <p
                v-for="point in chartPoints"
                :key="point.day"
              >
                {{ point.day }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>
